import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useRecaptchaAction } from "../lib/useRecaptchaAction";
import { RecaptchaDisclosure } from "../components/RecaptchaDisclosure";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const nav = useNavigate();
    const { getToken } = useRecaptchaAction();

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("A palavra-passe deve ter pelo menos 8 caracteres");
            return;
        }
        if (password !== confirm) {
            toast.error("As palavras-passe não coincidem");
            return;
        }
        setBusy(true);
        try {
            const recaptcha_token = await getToken("reset_password");
            await api.post("/auth/reset-password", { token, new_password: password, recaptcha_token });
            setDone(true);
            toast.success("Palavra-passe atualizada. Pode fazer login.");
            setTimeout(() => nav("/login"), 1500);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen grid lg:grid-cols-2" data-testid="reset-invalid-page">
                <aside className="relative bg-[#0d1628] text-white hidden lg:flex flex-col justify-between p-12 grain">
                    <Link to="/" className="font-display text-3xl tracking-tight">IHTP</Link>
                    <div className="font-display italic text-3xl max-w-md">
                        Link inválido ou expirado.
                    </div>
                    <div className="label-caps text-white/50">Recuperação de acesso</div>
                </aside>
                <section className="flex items-center justify-center px-6 md:px-12 py-16">
                    <div className="w-full max-w-md">
                        <h1 className="font-display text-4xl text-[#0d1628]">Token em falta</h1>
                        <p className="text-sm text-[#52525b] mt-3">O link de recuperação parece estar incompleto. Solicite um novo link.</p>
                        <Link to="/forgot-password" className="btn-primary mt-8 inline-flex" data-testid="request-new-link">
                            Solicitar novo link
                        </Link>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid="reset-password-page">
            <aside className="relative bg-[#0d1628] text-white hidden lg:flex flex-col justify-between p-12 grain">
                <Link to="/" className="font-display text-3xl tracking-tight">IHTP</Link>
                <div>
                    <div className="font-display text-4xl xl:text-5xl leading-[1.1] max-w-lg">
                        Recomeçar também é poder.
                    </div>
                    <div className="label-caps text-white/60 mt-8">Recuperação de acesso</div>
                </div>
                <div className="label-caps text-white/50">IHTP — I Have The Power</div>
            </aside>

            <section className="flex items-center justify-center px-6 md:px-12 py-16">
                <div className="w-full max-w-md">
                    <h1 className="font-display text-4xl text-[#0d1628]">Nova palavra-passe</h1>
                    <p className="text-sm text-[#52525b] mt-3">Defina a sua nova palavra-passe. Mínimo de 8 caracteres.</p>

                    {done ? (
                        <div className="card-flat mt-10" data-testid="reset-success-card">
                            <div className="label-caps">Sucesso</div>
                            <p className="text-[#0d1628] mt-3">A sua palavra-passe foi atualizada. A redirecionar para o login...</p>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="reset-form">
                            <div>
                                <label className="label-caps block mb-2">Nova palavra-passe</label>
                                <div className="relative">
                                    <input
                                        type={show ? "text" : "password"}
                                        required
                                        minLength={8}
                                        className="input-clean pr-10"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        data-testid="reset-password-input"
                                        autoComplete="new-password"
                                    />
                                    <button type="button" className="absolute right-0 top-1 text-[#52525b]" onClick={() => setShow(!show)} data-testid="toggle-password">
                                        {show ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Confirmar palavra-passe</label>
                                <input
                                    type={show ? "text" : "password"}
                                    required
                                    minLength={8}
                                    className="input-clean"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    data-testid="reset-confirm-input"
                                    autoComplete="new-password"
                                />
                            </div>
                            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="reset-submit">
                                {busy ? "A atualizar..." : "Definir nova palavra-passe"}
                            </button>
                            <div className="text-sm">
                                <Link to="/login" className="link-underline text-[#0d1628]">Voltar ao login</Link>
                            </div>
                            <RecaptchaDisclosure />
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}
